import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import QRCode from "qrcode";
import { Phone, Mail, Globe, Download, Share2, QrCode, Copy } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  PHONE_TEL, WHATSAPP_CARD_URL, EMAIL, EMAIL_MAILTO_PLAIN,
  WEBSITE_URL, WEBSITE_DISPLAY, CARD_URL, VCARD, buildVcf,
} from "@/lib/contact";
import { track } from "@/lib/track";

const QR_COLORS = { dark: "#0d2731", light: "#f6f3ec" };

const Carte = () => {
  const reduceMotion = useReducedMotion();
  const [qrOpen, setQrOpen] = useState(false);
  const [qrPng, setQrPng] = useState(null);
  const [qrSvg, setQrSvg] = useState(null);
  const [qrSmall, setQrSmall] = useState(null);

  useEffect(() => {
    QRCode.toDataURL(CARD_URL, { width: 1024, margin: 2, color: QR_COLORS }).then(setQrPng).catch(() => {});
    QRCode.toDataURL(CARD_URL, { width: 240, margin: 2, color: QR_COLORS }).then(setQrSmall).catch(() => {});
    QRCode.toString(CARD_URL, { type: "svg", margin: 2, color: QR_COLORS }).then(setQrSvg).catch(() => {});
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(CARD_URL);
      toast.success("Lien copié dans le presse-papiers");
    } catch {
      toast.error("Copie impossible — sélectionnez l'adresse dans la barre du navigateur");
    }
  };

  const share = async () => {
    track("share_card");
    if (navigator.share) {
      try {
        await navigator.share({ title: "AVELIS | Tarification infirmière", text: "Carte de visite AVELIS", url: CARD_URL });
      } catch {
        /* partage annulé */
      }
    } else {
      copyLink();
    }
  };

  const saveContact = async () => {
    track("save_contact_card");
    let photo = null;
    try {
      const res = await fetch("/avelis-logo-card.png");
      const blob = await res.blob();
      photo = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      /* vCard sans photo */
    }
    const blob = new Blob([buildVcf(photo)], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = VCARD.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Contact AVELIS téléchargé");
  };

  const downloadSvg = () => {
    if (!qrSvg) return;
    const blob = new Blob([qrSvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "AVELIS-QR-code.svg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const enter = reduceMotion
    ? {}
    : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

  return (
    <div data-testid="card-page" className="min-h-screen bg-[#0d2731] relative overflow-hidden">
      <Seo
        title="Carte de visite AVELIS | Tarification infirmière"
        description="Contactez AVELIS, votre partenaire en tarification infirmière. Téléphone, WhatsApp, e-mail et coordonnées en un clic."
      />
      <div className="absolute inset-0 dotted-bg-light opacity-60" aria-hidden="true" />
      <div className="absolute -top-28 -right-28 h-80 w-80 rounded-full border border-[#b5c4b1]/15" aria-hidden="true" />
      <div className="absolute -bottom-36 -left-24 h-96 w-96 rounded-full border border-[#b5c4b1]/10" aria-hidden="true" />

      <div className="relative min-h-screen flex items-center justify-center gap-16 px-4 py-10 pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
        <motion.div
          {...enter}
          className="w-full max-w-[440px] rounded-[28px] bg-[#f6f3ec] shadow-[0_50px_120px_-40px_rgba(0,0,0,0.6)] p-7 sm:p-9"
        >
          {/* A — Identité */}
          <div className="text-center">
            <img src="/avelis-logo-full.png" alt="AVELIS by DK Soins SRL" className="h-11 w-auto mx-auto" />
            <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.24em] text-[#176b70]">Tarification infirmière</p>
            <h1 className="mt-6 text-[1.65rem] sm:text-3xl font-extrabold tracking-tight leading-[1.15] text-[#0d2731]">
              Votre facturation simplifiée. Votre temps retrouvé.
            </h1>
            <p className="mt-4 text-sm sm:text-base text-[#4b585e] leading-relaxed">
              AVELIS accompagne les infirmiers indépendants dans leur facturation pour leur permettre de se concentrer sur l'essentiel : leurs patients.
            </p>

            <div data-testid="card-pricing" className="mt-6 rounded-2xl border border-[#b5c4b1]/60 bg-[#b5c4b1]/[0.12] px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#176b70]">Tarif unique</p>
              <p className="mt-1.5 text-2xl font-extrabold tracking-tight text-[#0d2731]">4 % TVAC</p>
              <p className="mt-0.5 text-sm font-bold text-[#4b585e]">des honoraires encaissés</p>
              <p className="mt-1 text-[11px] font-semibold text-[#4b585e]/75">+ frais de logiciel</p>
            </div>
          </div>

          {/* B — Actions principales */}
          <div className="mt-8 flex flex-col gap-3">
            <a
              href={PHONE_TEL}
              data-testid="card-call"
              onClick={() => track("click_phone_card")}
              className="btn-primary w-full !py-4 !text-base"
            >
              <Phone className="h-5 w-5" strokeWidth={2.2} />
              Appeler AVELIS
            </a>
            <a
              href={WHATSAPP_CARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="card-whatsapp"
              onClick={() => track("click_whatsapp_card")}
              className="btn-whatsapp w-full !py-4 !text-base"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Écrire sur WhatsApp
            </a>
          </div>

          {/* C — Autres contacts */}
          <div className="mt-6 grid grid-cols-1 gap-2.5">
            <a
              href={EMAIL_MAILTO_PLAIN}
              data-testid="card-email"
              onClick={() => track("click_email_card")}
              className="flex items-center gap-3.5 rounded-2xl border border-[#e5e1d8] bg-white px-5 py-4 transition-[border-color,transform] duration-200 hover:border-[#b5c4b1] hover:-translate-y-0.5"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#176b70]/10 text-[#176b70]">
                <Mail className="h-[18px] w-[18px]" strokeWidth={1.9} />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#4b585e]/70">E-mail</span>
                <span className="block text-sm font-bold text-[#0d2731] truncate">{EMAIL}</span>
              </span>
            </a>
            <a
              href={WEBSITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="card-website"
              onClick={() => track("click_website_card")}
              className="flex items-center gap-3.5 rounded-2xl border border-[#e5e1d8] bg-white px-5 py-4 transition-[border-color,transform] duration-200 hover:border-[#b5c4b1] hover:-translate-y-0.5"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#176b70]/10 text-[#176b70]">
                <Globe className="h-[18px] w-[18px]" strokeWidth={1.9} />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#4b585e]/70">Site internet</span>
                <span className="block text-sm font-bold text-[#0d2731]">{WEBSITE_DISPLAY}</span>
              </span>
            </a>
          </div>

          {/* D — Enregistrer le contact */}
          <button
            data-testid="card-save"
            onClick={saveContact}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0d2731] text-[#0d2731] px-6 py-3.5 text-sm font-bold tracking-wide transition-[background-color,color,transform] duration-300 hover:bg-[#0d2731] hover:text-[#f6f3ec] hover:-translate-y-0.5 active:translate-y-0"
          >
            <Download className="h-4 w-4" strokeWidth={2.2} />
            Ajouter AVELIS à mes contacts
          </button>

          {/* E — Partager */}
          <div className="mt-4 flex items-center gap-2.5">
            <button
              data-testid="card-share"
              onClick={share}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#176b70]/10 text-[#176b70] px-5 py-3.5 text-sm font-bold transition-[background-color,transform] duration-200 hover:bg-[#176b70]/20 hover:-translate-y-0.5"
            >
              <Share2 className="h-4 w-4" strokeWidth={2.2} />
              Partager ma carte
            </button>
            <button
              data-testid="card-qr-open"
              onClick={() => { track("qr_open_card"); setQrOpen(true); }}
              aria-label="Afficher le QR code"
              className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#0d2731]/[0.06] text-[#0d2731] transition-[background-color,transform] duration-200 hover:bg-[#0d2731]/12 hover:-translate-y-0.5"
            >
              <QrCode className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </motion.div>

        {/* QR latéral desktop */}
        <motion.aside
          {...(reduceMotion ? {} : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] } })}
          className="hidden lg:flex flex-col items-center text-center max-w-[260px]"
          data-testid="card-qr-aside"
        >
          <div className="rounded-3xl bg-[#f6f3ec] p-5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]">
            {qrSmall ? (
              <img src={qrSmall} alt="QR code vers la carte de visite AVELIS" className="h-44 w-44" />
            ) : (
              <div className="h-44 w-44 rounded-xl bg-[#e5e1d8]/60 animate-pulse" />
            )}
          </div>
          <p className="mt-5 text-sm font-bold text-[#f6f3ec]">Cette carte sur votre smartphone</p>
          <p className="mt-2 text-xs text-[#f6f3ec]/60 leading-relaxed">
            Scannez le QR code avec l'appareil photo de votre téléphone pour ouvrir la carte AVELIS.
          </p>
        </motion.aside>
      </div>

      {/* Modale QR */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent data-testid="qr-modal" className="bg-[#f6f3ec] border border-[#e5e1d8] rounded-3xl max-w-sm p-8 text-center">
          <DialogTitle className="sr-only">QR code de la carte AVELIS</DialogTitle>
          <img src="/avelis-logo-full.png" alt="AVELIS" className="h-8 w-auto mx-auto" />
          <div className="mt-6 rounded-2xl bg-white border border-[#e5e1d8] p-4 inline-block mx-auto">
            {qrPng ? (
              <img src={qrPng} alt="QR code pointant vers https://avelis.be/carte" className="h-52 w-52" />
            ) : (
              <div className="h-52 w-52 rounded-xl bg-[#e5e1d8]/60 animate-pulse" />
            )}
          </div>
          <p className="mt-5 text-sm font-bold text-[#0d2731]">Scannez pour retrouver AVELIS</p>
          <div className="mt-6 flex flex-col gap-2.5">
            <button data-testid="qr-copy" onClick={copyLink} className="btn-primary w-full !py-3">
              <Copy className="h-4 w-4" strokeWidth={2.2} />
              Copier le lien
            </button>
            {qrPng && (
              <a
                href={qrPng}
                download="AVELIS-QR-code.png"
                data-testid="qr-download-png"
                className="btn-ghost w-full !py-3"
              >
                <Download className="h-4 w-4" strokeWidth={2.2} />
                Télécharger le QR code (PNG HD)
              </a>
            )}
            {qrSvg && (
              <button
                data-testid="qr-download-svg"
                onClick={downloadSvg}
                className="text-sm font-bold text-[#176b70] hover:text-[#0d2731] transition-colors py-1"
              >
                Télécharger en SVG (impression)
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Carte;
